import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../../ThemeContext';
import { dataAPI } from '../../helpers/api_service';
import { APP_URLS } from '../../helpers/url_helper';
import './Subscription.css';

// Import SVG assets for plan icons - using same as PlanDetails
import GiftBox from '../../assets/gift-box 1.svg';
import StarterPlan from '../../assets/pricing-starter.svg';
import StandardPlan from '../../assets/pricing-standard.svg';
import PremiumPlan from '../../assets/pricing-premium.svg';
import Checkmark from '../../assets/checkmark.svg';
// Import new plan icons
import Gift1 from '../../assets/gift 1.svg';
import Crown from '../../assets/Crown.svg';
import Rocket1 from '../../assets/rocket 1.svg';

function SubscriptionPlans() {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [billingToggle, setBillingToggle] = useState('MONTHLY');
  const [userEmail, setUserEmail] = useState('');

  // Initialize theme on component mount
  useEffect(() => {
    document.body.classList.remove('dark-mode', 'light-mode');
    document.body.classList.add(mode === 'dark' ? 'dark-mode' : 'light-mode');
  }, [mode]);

  // Fetch subscription plans and user data on component mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const response = await dataAPI.getSubscriptionPlans(false); // include_trial = false for after-login page
        if (response.status === 'success' && response.data) {
          setPlans(response.data);
        } else {
          setError('Failed to load subscription plans');
        }
      } catch (err) {
        console.error('Error fetching subscription plans:', err);
        setError('Failed to load subscription plans');
      } finally {
        setLoading(false);
      }
    };

    const getUserEmail = () => {
      // Try to get user email from localStorage
      const userData = localStorage.getItem('userData');
      if (userData) {
        try {
          const user = JSON.parse(userData);
          setUserEmail(user.email || user.user_email || '');
        } catch (err) {
          console.error('Error parsing user data:', err);
        }
      }
    };

    fetchPlans();
    getUserEmail();
  }, []);

  // Helper function to get plan icon based on plan title
  const getPlanIcon = (planTitle) => {
    const title = planTitle.toLowerCase();
    if (title.includes('trial')) {
      return <img src={GiftBox} className="plan-icon" alt={`${planTitle} Plan`} />;
    } else if (title.includes('standard')) {
      return <img src={Gift1} className="plan-icon" alt={`${planTitle} Plan`} />;
    } else if (title.includes('gold')) {
      return <img src={Crown} className="plan-icon" alt={`${planTitle} Plan`} />;
    } else if (title.includes('platinum')) {
      return <img src={Rocket1} className="plan-icon" alt={`${planTitle} Plan`} />;
    }
    // Default fallback
    return <img src={GiftBox} className="plan-icon" alt={`${planTitle} Plan`} />;
  };

  // Helper function to format price display
  const formatPrice = (plan) => {
    if (!plan) {
      return {
        oldPrice: '',
        newPrice: '',
        period: ''
      };
    }

    const isEnterprisePlan = plan.is_enterprise || (plan.title && plan.title.toLowerCase().includes('enterprise'));
    if (isEnterprisePlan) {
      return {
        oldPrice: '',
        newPrice: 'Custom pricing',
        period: ''
      };
    }

    if (!plan.month_price) {
      return {
        oldPrice: '',
        newPrice: 'Free',
        period: '/Trial'
      };
    }
    
    if (billingToggle === 'YEARLY') {
      return {
        oldPrice: '',
        newPrice: `$${plan.year_price}`,
        period: '/Yearly'
      };
    }
    
    return {
      oldPrice: '',
      newPrice: `$${plan.month_price}`,
      period: '/Monthly'
    };
  };

  // Helper function to format features from description
  const formatFeatures = (formattedDescription) => {
    if (!formattedDescription) return [];
    
    return formattedDescription.split('\n').map((line, index) => {
      // Skip the first line as it's the title which we're using separately
      if (index === 0) return null;
      
      return (
        <li key={index} className="feature-item">
          <img src={Checkmark} className="feature-check" alt="check" />
          <span>{line}</span>
        </li>
      );
    }).filter(Boolean);
  };

  // Get plan description (first line of formatted_description)
  const getPlanDescription = (formattedDescription) => {
    if (!formattedDescription) return '';
    const lines = formattedDescription.split('\n');
    return lines[0] || '';
  };

  // Check if plan is most popular (highlight only Gold plan)
  const isMostPopular = (plan, index, totalPlans) => {
    const title = plan.title.toLowerCase();
    // Only highlight Gold plan
    return title.includes('gold');
  };

  // Handle plan selection and navigate to checkout
  const handlePlanSelection = (plan, formattedDescription = '') => {
    const isEnterprisePlan = plan.is_enterprise || (plan.title && plan.title.toLowerCase().includes('enterprise'));

    if (isEnterprisePlan) {
      const priceInfo = formatPrice(plan);
      navigate(APP_URLS.CONTACT_SALES, { 
        state: {
          planTitle: `${plan.title} Plan`,
          billingCycle: billingToggle,
          displayPrice: `${priceInfo.newPrice}${priceInfo.period}`,
          formattedDescription: formattedDescription,
          monthPrice: plan.month_price || null,
          yearPrice: plan.year_price || null,
        }
      });
      return;
    }

    // Don't process if it's a free trial plan
    if (!plan.month_price) {
      // For free trial, you might want to handle differently
      alert('Free trial activation will be implemented here');
      return;
    }

    // Prepare plan details for checkout
    const planDetails = {
      planId: plan.id,
      planTitle: plan.title,
      billingCycle: billingToggle.toLowerCase(),
      customerEmail: userEmail,
      displayPrice: formatPrice(plan).newPrice + formatPrice(plan).period,
      formattedDescription: formattedDescription
    };

    // Debug what we're sending to checkout
    console.log('=== SUBSCRIPTION PAGE - SENDING TO CHECKOUT ===');
    console.log('Plan:', plan);
    console.log('Formatted Description:', formattedDescription);
    console.log('Plan Details being sent:', planDetails);

    // Navigate to checkout session with plan details
    navigate(APP_URLS.CHECKOUT, { state: planDetails });
  };

  // Helper function to sort plans in correct order: Trial, Standard, Gold, Platinum
  const sortPlans = (plans) => {
    const planOrder = ['trial', 'standard', 'gold', 'platinum'];
    return [...plans].sort((a, b) => {
      const titleA = a.plan.title.toLowerCase();
      const titleB = b.plan.title.toLowerCase();
      
      const indexA = planOrder.findIndex(order => titleA.includes(order));
      const indexB = planOrder.findIndex(order => titleB.includes(order));
      
      // If both found, sort by index
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      // If only one found, put it first
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      // If neither found, maintain original order
      return 0;
    });
  };

  if (loading) {
    return (
      <div className="subscription-page">
        <Container className="text-center py-5">
          <h1 className="subscription-title">Choose Your Perfect Plan</h1>
          <p className="subscription-subtitle">Loading subscription plans...</p>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="subscription-page">
        <Container className="text-center py-5">
          <h1 className="subscription-title">Choose Your Perfect Plan</h1>
          <p className="subscription-subtitle">Error loading plans. Please try again later.</p>
        </Container>
      </div>
    );
  }

  return (
    <div className="subscription-page">
      <Container fluid className="subscription-container">
        {/* Header Section */}
        <div className="subscription-header text-center">
          <h1 className="subscription-title">Choose Your Perfect Plan</h1>
          <p className="subscription-subtitle">
            Pick a plan that fits your goals and unlock premium features anytime.
          </p>
          
          {/* Billing Toggle */}
          <div className="billing-toggle">
            <Button
              variant={billingToggle === 'MONTHLY' ? 'primary' : 'outline-secondary'}
              className={`toggle-btn ${billingToggle === 'MONTHLY' ? 'active' : ''}`}
              onClick={() => setBillingToggle('MONTHLY')}
            >
              MONTHLY
            </Button>
            <Button
              variant={billingToggle === 'YEARLY' ? 'primary' : 'outline-secondary'}
              className={`toggle-btn ${billingToggle === 'YEARLY' ? 'active' : ''}`}
              onClick={() => setBillingToggle('YEARLY')}
            >
              YEARLY
            </Button>
          </div>
        </div>

        {/* Plans Section */}
        <Row className="plans-row">
          {sortPlans(plans).map((planData, index) => {
            const { plan, formatted_description } = planData;
            const isPopular = isMostPopular(plan, index, sortPlans(plans).length);
            const priceInfo = formatPrice(plan);
            const isEnterprisePlan = plan.is_enterprise || (plan.title && plan.title.toLowerCase().includes('enterprise'));
            const features = formatFeatures(formatted_description);
            const description = getPlanDescription(formatted_description);
            
            return (
              <Col key={plan.id} className={`plan-col ${isPopular ? 'popular-column' : ''}`}>
                {isPopular ? (
                  <div className="popular-wrapper">
                    <div className="most-popular-badge">
                      Most Popular
                    </div>
                    <div className="plan-card">
                      <div className="plan-content">
                        {/* Plan Icon */}
                        <div className="plan-icon-container">
                          {getPlanIcon(plan.title)}
                        </div>

                        {/* Plan Title */}
                        <h3 className="plan-title">{plan.title} Plan</h3>
                        
                        {/* Plan Description */}
                        <p className="plan-description">{description}</p>

                        {/* Plan Pricing */}
                        <div className="plan-pricing">
                          {priceInfo.oldPrice && (
                            <span className="old-price">{priceInfo.oldPrice}</span>
                          )}
                          <div className="price-main">
                            <span className="new-price">{priceInfo.newPrice}</span>
                            <span className="price-period">{priceInfo.period}</span>
                          </div>
                        </div>

                        {/* Plan Features */}
                        <ul className="plan-features">
                          {features}
                        </ul>
                        
                        {/* Get Started Button */}
                        <div className="plan-button-container">
                          <button 
                            className="plan-button"
                            onClick={() => handlePlanSelection(plan, formatted_description)}
                          >
                            {isEnterprisePlan ? 'Contact Sales' : (plan.month_price ? 'Get Started' : 'Start Free Trial')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="plan-card">
                    <div className="plan-content">
                      {/* Plan Icon */}
                      <div className="plan-icon-container">
                        {getPlanIcon(plan.title)}
                      </div>

                      {/* Plan Title */}
                      <h3 className="plan-title">{plan.title} Plan</h3>
                      
                      {/* Plan Description */}
                      <p className="plan-description">{description}</p>

                      {/* Plan Pricing */}
                      <div className="plan-pricing">
                        {priceInfo.oldPrice && (
                          <span className="old-price">{priceInfo.oldPrice}</span>
                        )}
                        <div className="price-main">
                          <span className="new-price">{priceInfo.newPrice}</span>
                          <span className="price-period">{priceInfo.period}</span>
                        </div>
                      </div>

                      {/* Plan Features */}
                      <ul className="plan-features">
                        {features}
                      </ul>
                      
                      {/* Get Started Button */}
                      <div className="plan-button-container">
                        <button 
                          className="plan-button"
                          onClick={() => handlePlanSelection(plan, formatted_description)}
                        >
                          {isEnterprisePlan ? 'Contact Sales' : (plan.month_price ? 'Get Started' : 'Start Free Trial')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </Col>
            );
          })}
        </Row>
      </Container>
    </div>
  );
}

export default SubscriptionPlans;
